/**
 * aviCore.js — видео AVI (MJPEG) с УЗИ-аппарата → многокадровый УЗИ-DICOM.
 *
 * ПОВОД (12.0.41). Владелец выгрузил с Mindray DC-40 Exp эхокардиографию
 * одним файлом 202609251523180054CARD.AVI (34,6 МБ): 800×600, 35 кадров/с,
 * 213 кадров, кодек MJPEG — каждый кадр обычная картинка JPEG. Программа
 * такой файл молча выбрасывала: проверка ищет подпись DICM, у AVI её нет.
 *
 * ПОЧЕМУ В DICOM, А НЕ СВОЙ ПРОИГРЫВАТЕЛЬ. Многокадровый УЗИ-DICOM со сжатием
 * JPEG Baseline — самый обычный вид эхо, так пишут и GE, и Philips, и сам
 * Mindray, когда выгружает в DICOM. Программа его уже показывает как кино
 * (core/sopKind.js: US — кадры идут во времени). Кадры переносятся БАЙТ В
 * БАЙТ, без пережатия: JPEG из AVI становится фрагментом пикселей DICOM.
 * Дальше работает то, что уже есть: кино, инструменты, архив, экспорт.
 *
 * ОСОБЕННОСТЬ ФАЙЛА MINDRAY. Аппарат не дописывает размеры контейнеров:
 * RIFF объявлен в 80 байт, LIST movi — в 4 байта, а в файле 34 МБ. Разбор,
 * который верит этим числам, найдёт ноль кадров. Поэтому размеры RIFF и LIST
 * здесь не используются вовсе: в контейнер входим и идём по кускам подряд.
 * Размеры листовых кусков (avih, strh, strf, 00dc) у него верные — по ним и
 * шагаем.
 *
 * ЧЕГО ЗДЕСЬ НЕТ И ЧТО ВРАЧ ДОЛЖЕН ЗНАТЬ. В AVI нет масштаба: сколько
 * миллиметров в пикселе, файл не говорит. Шкала глубины впечатана в картинку.
 * [12.0.42] Если её нашли на первом кадре (core/echoSimpson.js, строгий
 * поиск), сюда приходит `ммНаПиксель`, и PixelSpacing пишется вместе с
 * пояснением в ImageComments — откуда число. Не нашли — PixelSpacing не
 * пишется НИКОГДА: выдуманный масштаб хуже отсутствующего. Данных пациента в
 * AVI тоже нет.
 *
 * [12.0.45] CINEPAK. Архивные клипы владельца (17 штук, два аппарата) сжаты
 * кодеком Cinepak («cvid»). Здесь их кадры только находятся (сырые куски, как
 * у MJPEG), а распаковывает io/cinepak.js — его зовёт io/aviImport.js и
 * передаёт сюда готовые RGB-кадры (сведения.кадрыRGB). Такой DICOM пишется
 * БЕЗ сжатия (Explicit VR Little Endian, RGB): пережимать в JPEG — терять ещё
 * раз поверх потерь Cinepak, а клипы короткие и маленькие (424×288, 25 кадров
 * — 9 МБ).
 *
 * Файл без импортов — правило проверяется тестом (tests/avi_test.mjs), а не
 * запуском.
 */

const US_MULTIFRAME = '1.2.840.10008.5.1.4.1.1.3.1';   // Ultrasound Multi-frame Image Storage
const JPEG_BASELINE = '1.2.840.10008.1.2.4.50';        // JPEG Baseline (Process 1)
const EXPLICIT_LE = '1.2.840.10008.1.2.1';             // [12.0.45] без сжатия — кадры Cinepak, распакованные в RGB

/** Четыре байта как текст. */
function fourcc(b, o) {
  return String.fromCharCode(b[o], b[o + 1], b[o + 2], b[o + 3]);
}

const ПЕЧАТНЫЙ = /^[\x20-\x7e]{4}$/;          // имя куска — четыре печатных знака
const КУСОК_КАДРА = /^[0-9a-f]{2}d[bc]$/i;     // 00dc — сжатый кадр, 00db — несжатый

/** Подпись RIFF…AVI в первых двенадцати байтах. */
export function этоAVI(bytes) {
  return !!bytes && bytes.length >= 12 && fourcc(bytes, 0) === 'RIFF' && fourcc(bytes, 8) === 'AVI ';
}

/**
 * Найти следующий кусок кадра нужного потока, начиная с позиции from.
 * Нужен, когда посреди файла мусор или нули: идём по байтам до знакомой
 * подписи, у которой и размер помещается в файл, и данные начинаются с JPEG.
 * @returns {number} позиция или -1
 */
function найтиКадр(b, from, поток, cinepak = false) {
  const d0 = поток.charCodeAt(0), d1 = поток.charCodeAt(1);
  const n = b.length;
  for (let i = from; i + 12 <= n; i++) {
    if (b[i] !== d0 || b[i + 1] !== d1 || b[i + 2] !== 0x64) continue;       // 'd'
    if (b[i + 3] !== 0x63 && b[i + 3] !== 0x62) continue;                      // 'c' | 'b'
    const size = b[i + 4] | (b[i + 5] << 8) | (b[i + 6] << 16) | (b[i + 7] << 24);
    if (size > 0 && i + 8 + size <= n && b[i + 8] === 0xff && b[i + 9] === 0xd8) return i;
    if (cinepak && size >= 10 && i + 8 + size <= n && этоКадрCinepak(b, i + 8, size)) return i;
  }
  return -1;
}

/**
 * [12.0.44] Есть ли у кадра конец JPEG (FFD9). Ищем в последних 64 байтах —
 * после конца аппараты кладут добивку (у Mindray — 1–3 нуля).
 *
 * ПОВОД. В полном файле владельца (34,6 МБ) целы кадры 1–144; с середины
 * кадра 145, ровно с границы кластера флешки (24 698 880 = 3015 × 8192), в
 * файле лежат чужие данные. Кадр 145 начинается как JPEG, а конца у него нет,
 * и 12.0.41–12.0.43 брали его в кино — на экране он серый. Теперь такой кадр
 * отбрасывается, и об этом сказано.
 */
export function естьКонецJPEG(b, off, len) {
  const конец = off + len;
  for (let i = Math.max(off + 2, конец - 64); i + 1 < конец; i++) if (b[i] === 0xff && b[i + 1] === 0xd9) return true;
  return false;
}

/**
 * [12.0.45] Похоже ли на кадр Cinepak: длина в заголовке кадра (байты 1–3) не
 * больше куска и не меньше заголовка, полос от 1 до 32. Кадр, у которого
 * заявленная длина больше куска, — обрезан.
 */
export function этоКадрCinepak(b, off, len) {
  if (len < 10) return false;
  const заявлено = (b[off + 1] << 16) | (b[off + 2] << 8) | b[off + 3];
  const полос = (b[off + 8] << 8) | b[off + 9];
  return заявлено >= 10 && заявлено <= len && полос >= 1 && полос <= 32;
}

/**
 * Маркеры JPEG до начала данных (SOS): вид сжатия, размеры, есть ли таблицы
 * Хаффмана. Нужен, чтобы отказать ЧЕСТНО и заранее, а не чёрным окном потом.
 */
export function заголовокJPEG(b, off, len) {
  const end = off + len;
  if (len < 4 || b[off] !== 0xff || b[off + 1] !== 0xd8) return null;
  const итог = { sof: 0, бит: 0, ширина: 0, высота: 0, компонент: 0, естьDHT: false };
  let p = off + 2;
  while (p + 4 <= end) {
    if (b[p] !== 0xff) return итог;              // сбились с разметки — отдаём, что успели
    const m = b[p + 1];
    if (m === 0xff) { p++; continue; }           // заполняющие FF допустимы
    if (m === 0xd9 || m === 0xda) return итог;   // конец картинки или начало данных
    const L = (b[p + 2] << 8) | b[p + 3];
    if (m >= 0xc0 && m <= 0xcf && m !== 0xc4 && m !== 0xc8 && m !== 0xcc) {
      итог.sof = m;
      итог.бит = b[p + 4];
      итог.высота = (b[p + 5] << 8) | b[p + 6];
      итог.ширина = (b[p + 7] << 8) | b[p + 8];
      итог.компонент = b[p + 9];
    } else if (m === 0xc4) {
      итог.естьDHT = true;
    }
    p += 2 + L;
  }
  return итог;
}

/**
 * Разобрать AVI: где лежат кадры видео, размер, частота кадров.
 * Кадры НЕ копируются — возвращаются смещения внутри bytes.
 *
 * @param {Uint8Array} bytes весь файл
 * @returns {{ширина:number, высота:number, кадровВСекунду:number, мсНаКадр:number,
 *            заявленоКадров:number, кодек:string, компонент:number,
 *            кадры:Array<{смещение:number, длина:number}>, замечания:string[]}}
 * @throws {Error} с объяснением по-русски, если открыть нельзя
 */
export function разобратьAVI(bytes) {
  if (!этоAVI(bytes)) throw new Error('это не AVI: в начале нет подписи RIFF…AVI');
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const n = bytes.length;
  const u32 = (o) => dv.getUint32(o, true);

  let мкс = 0, заявлено = 0, ширинаЗаявл = 0, высотаЗаявл = 0, ширинаStrf = 0, высотаStrf = 0;
  let scale = 0, rate = 0, кодек = '';
  let потоков = 0, видео = -1;
  const кадры = [];
  const замечания = [];
  let пустых = 0, неJPEG = 0, пропущеноБайт = 0;
  const оборванные = [];   // [12.0.44] номера кадров (с 1) без конца JPEG

  let pos = 12;
  while (pos + 8 <= n) {
    const id = fourcc(bytes, pos);
    const size = u32(pos + 4);

    // Контейнер: входим, размер не читаем (у Mindray он ложный — см. шапку).
    if (id === 'RIFF' || id === 'LIST') { pos += 12; continue; }

    const поток = String(Math.max(видео, 0)).padStart(2, '0');
    if (!ПЕЧАТНЫЙ.test(id) || pos + 8 + size > n) {
      // Кадр, который вылезает за конец файла, — обрезан (так и у присланного
      // начала файла: последние байты кадра не поместились).
      if (КУСОК_КАДРА.test(id) && id.slice(0, 2) === поток) {
        замечания.push('последний кадр обрезан на конце файла и пропущен');
        break;
      }
      // Мусор или нули. Ищем следующий кадр дальше по файлу.
      const дальше = найтиКадр(bytes, pos + 1, поток, кодек.toLowerCase() === 'cvid');
      if (дальше < 0) {
        let ненулевых = 0;
        for (let i = pos; i < n; i++) if (bytes[i] !== 0) { ненулевых++; break; }
        if (ненулевых) замечания.push(`в конце файла ${n - pos} байт не разобраны`);
        break;
      }
      пропущеноБайт += дальше - pos;
      pos = дальше;
      continue;
    }

    const d = pos + 8;
    if (id === 'avih' && size >= 40) {
      мкс = u32(d);
      заявлено = u32(d + 16);
      ширинаЗаявл = u32(d + 32);
      высотаЗаявл = u32(d + 36);
    } else if (id === 'strh' && size >= 36) {
      if (видео < 0 && fourcc(bytes, d) === 'vids') {
        видео = потоков;
        кодек = fourcc(bytes, d + 4).trim();
        scale = u32(d + 20);
        rate = u32(d + 24);
      }
      потоков++;
    } else if (id === 'strf' && видео >= 0 && видео === потоков - 1 && size >= 20) {
      // BITMAPINFOHEADER.biCompression — точнее, чем обработчик из strh.
      const c = fourcc(bytes, d + 16);
      if (ПЕЧАТНЫЙ.test(c)) кодек = c.trim();
      else if (u32(d + 16) === 0) кодек = 'RGB';
      ширинаStrf = dv.getInt32(d + 4, true);
      высотаStrf = Math.abs(dv.getInt32(d + 8, true));   // знак — порядок строк, нам не нужен
    } else if (КУСОК_КАДРА.test(id) && id.slice(0, 2) === поток) {
      if (size === 0) {
        // Пустой кусок в AVI значит «повторить прошлый кадр». Время важно
        // (кино должно идти со своей скоростью), поэтому повторяем.
        if (кадры.length) { кадры.push(кадры[кадры.length - 1]); пустых++; }
      } else if (bytes[d] === 0xff && bytes[d + 1] === 0xd8) {
        if (естьКонецJPEG(bytes, d, size)) кадры.push({ смещение: d, длина: size });
        else оборванные.push(кадры.length + оборванные.length + 1);
      } else if (кодек.toLowerCase() === 'cvid') {
        // [12.0.45] Cinepak: кадр цел, если его заявленная длина помещается в кусок.
        if (этоКадрCinepak(bytes, d, size)) кадры.push({ смещение: d, длина: size });
        else оборванные.push(кадры.length + оборванные.length + 1);
      } else {
        неJPEG++;
      }
    }
    pos = d + size + (size & 1);                 // куски RIFF выровнены по два байта
  }

  const имяКодека = кодек || 'неизвестный';
  if (!кадры.length) {
    if (неJPEG) {
      throw new Error(кодек === 'RGB'
        ? 'видео записано без сжатия (RGB) — программа пока открывает только AVI с MJPEG'
        : `видео сжато кодеком «${имяКодека}» — программа открывает AVI с MJPEG (каждый кадр — JPEG) и Cinepak`);
    }
    throw new Error('в AVI не найдено ни одного кадра видео');
  }
  if (неJPEG) замечания.push(`кадров не в JPEG пропущено: ${неJPEG}`);
  if (пустых) замечания.push(`повторов прошлого кадра: ${пустых}`);
  if (пропущеноБайт) замечания.push(`пропущено нечитаемых байт посреди файла: ${пропущеноБайт}`);
  const cinepak = имяКодека.toLowerCase() === 'cvid';
  if (оборванные.length) {
    замечания.push(`повреждённых кадров (обрыв ${cinepak ? 'кадра Cinepak' : 'JPEG'}) пропущено: ${оборванные.length}, первый — № ${оборванные[0]}`);
  }

  // Частота: из потока (rate/scale), иначе из avih, иначе 25 — с оговоркой.
  let кадровВСекунду = (rate > 0 && scale > 0) ? rate / scale : (мкс > 0 ? 1e6 / мкс : 0);
  if (!(кадровВСекунду > 0 && кадровВСекунду < 1000)) {
    кадровВСекунду = 25;
    замечания.push('частота кадров в файле не указана — взято 25 кадров/с');
  }
  const неполон = заявлено > 0 && кадры.length < заявлено
    ? { найдено: кадры.length, заявлено, секунд: кадры.length / кадровВСекунду, секундЗаявлено: заявлено / кадровВСекунду }
    : null;
  if (неполон) замечания.push(`в заголовке ${заявлено} кадров, целых найдено ${кадры.length} — файл неполон или повреждён`);

  if (cinepak) {
    // [12.0.45] Размер — из заголовка картинки потока (strf), иначе из avih.
    const ширина = ширинаStrf || ширинаЗаявл, высота = высотаStrf || высотаЗаявл;
    if (!(ширина > 0 && высота > 0 && ширина <= 4096 && высота <= 4096)) throw new Error('видео Cinepak без размеров кадра в заголовке');
    return { ширина, высота, компонент: 3, кадровВСекунду, мсНаКадр: 1000 / кадровВСекунду, заявленоКадров: заявлено,
      кодек: имяКодека, сжатие: 'cinepak', кадры, замечания, оборванные, неполон };
  }

  // Размер и вид сжатия — по самому JPEG: это и будет расшифровывать программа.
  const j = заголовокJPEG(bytes, кадры[0].смещение, кадры[0].длина);
  if (!j || !j.sof) throw new Error('первый кадр не похож на JPEG: нет заголовка кадра (SOF)');
  if (j.sof !== 0xc0 || j.бит !== 8) {
    throw new Error(`кадры JPEG не базового вида (маркер SOF${(j.sof - 0xc0).toString(16)}, ${j.бит} бит) — такие программа пока не открывает`);
  }
  if (j.компонент !== 1 && j.компонент !== 3) {
    throw new Error(`у кадров ${j.компонент} цветовых компонент — ожидалось 1 или 3`);
  }
  if (!j.естьDHT) {
    throw new Error('кадры MJPEG без таблиц Хаффмана — такой вариант MJPEG программа пока не открывает');
  }
  if ((ширинаЗаявл && ширинаЗаявл !== j.ширина) || (высотаЗаявл && высотаЗаявл !== j.высота)) {
    замечания.push(`в заголовке AVI ${ширинаЗаявл}×${высотаЗаявл}, в кадрах ${j.ширина}×${j.высота} — взято из кадров`);
  }

  // [12.0.44] Целых кадров меньше, чем обещает заголовок (неполон, выше), —
  // файл неполон или повреждён. Врач должен узнать это сразу.
  return {
    ширина: j.ширина,
    высота: j.высота,
    компонент: j.компонент,
    кадровВСекунду,
    мсНаКадр: 1000 / кадровВСекунду,
    заявленоКадров: заявлено,
    кодек: имяКодека,
    сжатие: 'jpeg',
    кадры,
    замечания,
    оборванные,
    неполон,
  };
}

// ══════════════════════════════════════════════════════════════
//  СВЕДЕНИЯ: дата, UID
// ══════════════════════════════════════════════════════════════

const два = (x) => String(x).padStart(2, '0');

/**
 * Дата и время съёмки. Mindray пишет их в начало имени:
 * 202609251523180054CARD.AVI → 2026-09-25 15:23:18. Не вышло — берём время
 * изменения файла (местное).
 * @returns {{дата:string, время:string}} DA и TM
 */
export function датаСъёмки(имя, изменёнМс) {
  const m = /^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/.exec(String(имя || ''));
  if (m) {
    const [, г, мес, д, ч, мин, с] = m.map(Number);
    if (г >= 1990 && мес >= 1 && мес <= 12 && д >= 1 && д <= 31 && ч < 24 && мин < 60 && с < 60) {
      return { дата: m[1] + m[2] + m[3], время: m[4] + m[5] + m[6] };
    }
  }
  const t = new Date(Number.isFinite(изменёнМс) && изменёнМс > 0 ? изменёнМс : 0);
  return {
    дата: `${t.getFullYear()}${два(t.getMonth() + 1)}${два(t.getDate())}`,
    время: `${два(t.getHours())}${два(t.getMinutes())}${два(t.getSeconds())}`,
  };
}

/**
 * UID из строки — всегда один и тот же для одной строки. Постоянство нужно:
 * пометки и линейки программа хранит по UID снимка, и при повторном
 * открытии того же AVI они должны найтись. Корень 2.25 — стандартный
 * (DICOM PS3.5 B.2): UID из 128-битного числа.
 */
export function uidИз(строка) {
  let h1 = 1779033703, h2 = 3144134277, h3 = 1013904242, h4 = 2773480762;
  const s = String(строка);
  for (let i = 0; i < s.length; i++) {
    const k = s.charCodeAt(i);
    h1 = h2 ^ Math.imul(h1 ^ k, 597399067);
    h2 = h3 ^ Math.imul(h2 ^ k, 2869860233);
    h3 = h4 ^ Math.imul(h3 ^ k, 951274213);
    h4 = h1 ^ Math.imul(h4 ^ k, 2716044179);
  }
  h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067);
  h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233);
  h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213);
  h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179);
  h1 ^= (h2 ^ h3 ^ h4); h2 ^= h1; h3 ^= h1; h4 ^= h1;
  const big = [h1, h2, h3, h4].reduce((acc, h) => (acc << 32n) | BigInt(h >>> 0), 0n);
  return '2.25.' + big.toString();
}

// ══════════════════════════════════════════════════════════════
//  ЗАПИСЬ DICOM (явный VR, little endian; пиксели — фрагменты JPEG)
// ══════════════════════════════════════════════════════════════

const ДЛИННЫЕ_VR = new Set(['OB', 'OW', 'OF', 'OD', 'OL', 'SQ', 'UC', 'UN', 'UR', 'UT']);
const utf8 = new TextEncoder();

/** Текстовое значение: UTF-8, чётная длина (UI добивается нулём, прочие — пробелом). */
function текст(vr, s) {
  const b = utf8.encode(String(s ?? ''));
  if (b.length % 2 === 0) return b;
  const out = new Uint8Array(b.length + 1);
  out.set(b);
  out[b.length] = vr === 'UI' ? 0 : 0x20;
  return out;
}

function эл(group, element, vr, value) {
  const длинный = ДЛИННЫЕ_VR.has(vr);
  const шапка = длинный ? 12 : 8;
  const out = new Uint8Array(шапка + value.length);
  const v = new DataView(out.buffer);
  v.setUint16(0, group, true);
  v.setUint16(2, element, true);
  out[4] = vr.charCodeAt(0);
  out[5] = vr.charCodeAt(1);
  if (длинный) v.setUint32(8, value.length, true);
  else v.setUint16(6, value.length, true);
  out.set(value, шапка);
  return { тег: (group << 16 | element) >>> 0, байты: out };
}

const стр = (g, e, vr, s) => эл(g, e, vr, текст(vr, s));
function us(g, e, x) {
  const b = new Uint8Array(2);
  new DataView(b.buffer).setUint16(0, x, true);
  return эл(g, e, 'US', b);
}

function склеить(куски) {
  const out = new Uint8Array(куски.reduce((s, k) => s + k.length, 0));
  let p = 0;
  for (const k of куски) { out.set(k, p); p += k.length; }
  return out;
}

/** Шапка куска инкапсулированных пикселей (FFFE,E000) или конец (FFFE,E0DD). */
function элементЭлемента(элемент, длина) {
  const b = new Uint8Array(8);
  const v = new DataView(b.buffer);
  v.setUint16(0, 0xfffe, true);
  v.setUint16(2, элемент, true);
  v.setUint32(4, длина, true);
  return b;
}

/** Постоянный UID «кто записал» — одна строка на все файлы программы. */
const IMPLEMENTATION_UID = uidИз('mikhmel-viewer:avi-import');

/**
 * Собрать многокадровый УЗИ-DICOM из разобранного AVI.
 *
 * Возвращает КУСКИ, а не один массив: кадры — это окна в исходный файл
 * (subarray), они не копируются здесь. Склеит их тот, кто сделает из кусков
 * Blob/File, — один раз.
 *
 * @param {ReturnType<typeof разобратьAVI>} разбор
 * @param {Uint8Array} bytes тот же файл, что разбирался
 * @param {{имяФайла:string, размер?:number, изменён?:number, ключИсследования?:string, номерСерии?:number,
 *          ммНаПиксель?:number, пояснениеМасштаба?:string}} сведения
 *   ммНаПиксель — [12.0.42] только найденный на шкале глубины; нет — PixelSpacing не пишется
 * @returns {{части: Uint8Array[], байт: number, uid: {исследование:string, серия:string, снимок:string}}}
 */
export function собратьDICOM(разбор, bytes, сведения = {}) {
  const имя = String(сведения.имяФайла || 'video.avi');
  const ключФайла = `${имя}|${сведения.размер ?? bytes.length}|${сведения.изменён ?? ''}`;
  const uid = {
    исследование: uidИз('study|' + (сведения.ключИсследования || ключФайла)),
    серия: uidИз('series|' + ключФайла),
    снимок: uidИз('sop|' + ключФайла),
  };
  const { дата, время } = датаСъёмки(имя, сведения.изменён);
  const цвет = разбор.компонент === 3;
  // [12.0.45] Cinepak — распакованные кадры RGB без сжатия.
  const rgb = разбор.сжатие === 'cinepak';
  const кадрыRGB = rgb ? (сведения.кадрыRGB || []) : null;
  if (rgb && !кадрыRGB.length) throw new Error('для Cinepak нужны распакованные кадры (сведения.кадрыRGB)');
  const размерКадра = разбор.ширина * разбор.высота * 3;
  if (rgb && кадрыRGB.some((к) => !к || к.length !== размерКадра)) throw new Error('распакованный кадр Cinepak не того размера');
  const кадров = rgb ? кадрыRGB.length : разбор.кадры.length;
  // DS не длиннее 16 знаков; три знака после точки хватает с запасом.
  const мс = String(+разбор.мсНаКадр.toFixed(3));
  const кс = String(Math.round(разбор.кадровВСекунду));
  const описание = ('AVI ' + имя).slice(0, 64);      // LO — не длиннее 64 знаков
  // [12.0.42] Масштаб — только найденный на шкале глубины. DS не длиннее 16 знаков.
  const мм = Number(сведения.ммНаПиксель);
  const масштаб = (мм > 0 && мм < 10) ? String(Number(мм.toPrecision(8))) : '';
  const пояснение = масштаб ? String(сведения.пояснениеМасштаба || 'масштаб со шкалы глубины на кадре').slice(0, 1024) : '';

  // Метагруппа 0002.
  const мета = [
    эл(0x0002, 0x0001, 'OB', new Uint8Array([0, 1])),
    стр(0x0002, 0x0002, 'UI', US_MULTIFRAME),
    стр(0x0002, 0x0003, 'UI', uid.снимок),
    стр(0x0002, 0x0010, 'UI', rgb ? EXPLICIT_LE : JPEG_BASELINE),
    стр(0x0002, 0x0012, 'UI', IMPLEMENTATION_UID),
    стр(0x0002, 0x0013, 'SH', 'MIKHMEL_AVI'),
  ];
  const длинаМеты = мета.reduce((s, x) => s + x.байты.length, 0);
  const ul = new Uint8Array(4);
  new DataView(ul.buffer).setUint32(0, длинаМеты, true);

  // Набор данных. Порядок тегов — по возрастанию, это требование стандарта;
  // ниже он дополнительно сортируется, чтобы правка списка его не сломала.
  const набор = [
    стр(0x0008, 0x0005, 'CS', 'ISO_IR 192'),            // строки в UTF-8 (имя файла может быть кириллицей)
    стр(0x0008, 0x0008, 'CS', 'DERIVED\\PRIMARY'),
    стр(0x0008, 0x0016, 'UI', US_MULTIFRAME),
    стр(0x0008, 0x0018, 'UI', uid.снимок),
    стр(0x0008, 0x0020, 'DA', дата),
    стр(0x0008, 0x0021, 'DA', дата),
    стр(0x0008, 0x0023, 'DA', дата),
    стр(0x0008, 0x0030, 'TM', время),
    стр(0x0008, 0x0031, 'TM', время),
    стр(0x0008, 0x0033, 'TM', время),
    стр(0x0008, 0x0050, 'SH', ''),
    стр(0x0008, 0x0060, 'CS', 'US'),
    стр(0x0008, 0x0070, 'LO', ''),
    стр(0x0008, 0x0090, 'PN', ''),
    стр(0x0008, 0x1030, 'LO', 'Видео AVI'),
    стр(0x0008, 0x103e, 'LO', описание),
    стр(0x0010, 0x0010, 'PN', ''),
    стр(0x0010, 0x0020, 'LO', ''),
    стр(0x0010, 0x0030, 'DA', ''),
    стр(0x0010, 0x0040, 'CS', ''),
    стр(0x0018, 0x0040, 'IS', кс),                       // Cine Rate
    стр(0x0018, 0x1063, 'DS', мс),                       // Frame Time, мс
    стр(0x0020, 0x000d, 'UI', uid.исследование),
    стр(0x0020, 0x000e, 'UI', uid.серия),
    стр(0x0020, 0x0010, 'SH', ''),
    стр(0x0020, 0x0011, 'IS', String(сведения.номерСерии || 1)),
    стр(0x0020, 0x0013, 'IS', '1'),
    стр(0x0020, 0x0020, 'CS', ''),
    ...(масштаб ? [стр(0x0020, 0x4000, 'LT', пояснение)] : []),   // Image Comments: откуда масштаб
    us(0x0028, 0x0002, цвет ? 3 : 1),
    стр(0x0028, 0x0004, 'CS', rgb ? 'RGB' : (цвет ? 'YBR_FULL_422' : 'MONOCHROME2')),
    ...(цвет ? [us(0x0028, 0x0006, 0)] : []),
    стр(0x0028, 0x0008, 'IS', String(кадров)),
    эл(0x0028, 0x0009, 'AT', new Uint8Array([0x18, 0x00, 0x63, 0x10])),  // шаг кадров — Frame Time (0018,1063)
    us(0x0028, 0x0010, разбор.высота),
    us(0x0028, 0x0011, разбор.ширина),
    ...(масштаб ? [стр(0x0028, 0x0030, 'DS', `${масштаб}\\${масштаб}`)] : []),  // Pixel Spacing: строки\столбцы
    us(0x0028, 0x0100, 8),
    us(0x0028, 0x0101, 8),
    us(0x0028, 0x0102, 7),
    us(0x0028, 0x0103, 0),
    стр(0x0028, 0x0301, 'CS', 'YES'),                    // надписи впечатаны в картинку
    стр(0x0028, 0x2110, 'CS', '01'),                     // сжатие с потерями (было ещё в AVI)
    ...(rgb ? [] : [стр(0x0028, 0x2114, 'CS', 'ISO_10918_1')]),   // у Cinepak своего кода метода в DICOM нет
  ].sort((a, b) => a.тег - b.тег);

  // Пиксели: OB неопределённой длины, пустая таблица смещений, по фрагменту на
  // кадр. Таблицу смещений оставляем ПУСТОЙ намеренно: при «один фрагмент —
  // один кадр» загрузчик Cornerstone 5.6.3 отрезает кадр копией
  // (wadouri/getEncapsulatedImageFrame), а с заполненной таблицей отдаёт
  // окно во весь файл — и в воркер уезжал бы весь файл на каждый кадр.
  const шапкаПикселей = new Uint8Array(12);
  const пв = new DataView(шапкаПикселей.buffer);
  пв.setUint16(0, 0x7fe0, true);
  пв.setUint16(2, 0x0010, true);
  шапкаПикселей[4] = 0x4f; шапкаПикселей[5] = 0x42;    // 'OB'
  if (rgb) {
    // [12.0.45] Без сжатия: OB известной длины, кадры подряд, длина чётная.
    const всего = размерКадра * кадров;
    пв.setUint32(8, всего + (всего & 1), true);
    const голова = склеить([new Uint8Array(128), new Uint8Array([0x44, 0x49, 0x43, 0x4d]), эл(0x0002, 0x0000, 'UL', ul).байты,
      ...мета.map((x) => x.байты), ...набор.map((x) => x.байты), шапкаПикселей]);
    const части = [голова, ...кадрыRGB];
    if (всего & 1) части.push(new Uint8Array(1));
    return { части, байт: части.reduce((s, ч) => s + ч.length, 0), uid };
  }
  пв.setUint32(8, 0xffffffff, true);

  const голова = склеить([
    new Uint8Array(128),
    new Uint8Array([0x44, 0x49, 0x43, 0x4d]),           // DICM
    эл(0x0002, 0x0000, 'UL', ul).байты,
    ...мета.map((x) => x.байты),
    ...набор.map((x) => x.байты),
    шапкаПикселей,
    элементЭлемента(0xe000, 0),                         // пустая Basic Offset Table
  ]);

  const части = [голова];
  const нуль = new Uint8Array(1);
  for (const к of разбор.кадры) {
    const нечёт = к.длина & 1;                          // фрагмент обязан быть чётной длины
    части.push(элементЭлемента(0xe000, к.длина + нечёт));
    части.push(bytes.subarray(к.смещение, к.смещение + к.длина));
    if (нечёт) части.push(нуль);
  }
  части.push(элементЭлемента(0xe0dd, 0));

  return { части, байт: части.reduce((s, ч) => s + ч.length, 0), uid };
}

export default { этоAVI, разобратьAVI, заголовокJPEG, этоКадрCinepak, собратьDICOM, датаСъёмки, uidИз };
